import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ColumnConfig } from "@/pages/Private/pages/List/schema/columnConfig";
import { buildSnippetToken } from "@/pages/Private/pages/Messages/components/AiSnippets/utils/snippetTokens";
import {
	buildSpintaxRows,
	normalizeSpintaxSearch,
} from "@/pages/Private/pages/Messages/components/Spintax/utils/spintaxRows";

import { FooterPopoverSection } from "../utils/editorFooter.types";

interface UseSpintaxFooterItemsArgs {
	listColumns?: ColumnConfig[];
	search: string;
	onInsert: (token: string) => void;
	onCreateNew?: () => void;
}

/** Spintax (RANDOM_VALUE column) sections of the footer's popover. */
export const useSpintaxFooterItems = ({
	listColumns,
	search,
	onInsert,
	onCreateNew,
}: UseSpintaxFooterItemsArgs): FooterPopoverSection[] => {
	const { t } = useTranslation();

	const rows = useMemo(
		() => buildSpintaxRows({ listColumns, insertedColumnIds: [] }),
		[listColumns]
	);

	return useMemo(() => {
		const normalizedSearch = normalizeSpintaxSearch(search);

		const sections: FooterPopoverSection[] = [
			{
				label: t("editorFooter.sections.available"),
				items: rows
					.filter((row) => !normalizedSearch || row.name.toLowerCase().includes(normalizedSearch))
					.map((row) => ({
						key: row.key,
						name: row.name,
						meta: t("spintax.section.variations", { count: row.variationCount }),
						onSelect: () => onInsert(buildSnippetToken(row.columnId, row.name)),
					})),
			},
		];

		if (onCreateNew) {
			sections.push({
				label: t("editorFooter.sections.create"),
				items: [
					{
						key: "create-spintax",
						name: t("editorFooter.newSpintax"),
						isCreateAction: true,
						onSelect: onCreateNew,
					},
				],
			});
		}

		return sections;
	}, [rows, search, onInsert, onCreateNew, t]);
};
